import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAgents, useAgent, useAgentActions, useAgentProfiles } from '@/hooks/useAgents';
import { useAuth } from '@/context/AuthContext';
import { AgentSession, AgentState, LogLine, getAgentLog } from '@/api/agents.api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Bot, Pause, Play, Send, Square, Terminal } from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';

/**
 * Who's doing what among the AI sessions (plan UC-12, §9 Agents page): each
 * session's issue, step, cost against its budget, and who it waits on. Messages
 * typed here go into the session's inbox and become its next turn.
 */

const STATE_STYLE: Record<AgentState, string> = {
  queued: 'bg-slate-100 text-slate-700',
  running: 'bg-green-100 text-green-800',
  idle: 'bg-blue-50 text-blue-700',
  waiting_human: 'bg-amber-100 text-amber-800',
  paused: 'bg-orange-100 text-orange-800',
  done: 'bg-slate-100 text-slate-500',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

const STATE_LABEL: Record<AgentState, string> = {
  queued: 'queued',
  running: 'working',
  idle: 'idle',
  waiting_human: 'waiting for a person',
  paused: 'paused',
  done: 'done',
  failed: 'failed',
  cancelled: 'stopped',
};

function ref(s: AgentSession): { text: string; link?: string } {
  const project = s.projectPath?.split('/').pop() || String(s.gitlabProjectId);
  if (s.iid) return { text: `${project}#${s.iid}`, link: `/issues/${encodeURIComponent(project)}/${s.iid}` };
  return { text: `${project}!${s.mrIid}` };
}

function limitedUntil(s: AgentSession): Date | null {
  return s.resumeAfter && new Date(s.resumeAfter) > new Date() ? new Date(s.resumeAfter) : null;
}

/** Steps against the turn budget on a subscription, dollars against the budget otherwise */
function budget(s: AgentSession): { label: string; share: number } {
  if (s.billing === 'subscription') {
    return { label: `${s.turns} / ${s.turnBudget || '∞'} steps`, share: s.turnBudget ? Math.min(100, Math.round((s.turns / s.turnBudget) * 100)) : 0 };
  }
  return { label: `$${s.costUsd.toFixed(2)} / $${s.budgetUsd.toFixed(2)}`, share: s.budgetUsd ? Math.min(100, Math.round((s.costUsd / s.budgetUsd) * 100)) : 0 };
}

function SessionRow({ s, selected, onSelect }: { s: AgentSession; selected: boolean; onSelect: () => void }) {
  const r = ref(s);
  const { label, share: budgetShare } = budget(s);
  const limit = limitedUntil(s);
  return (
    <tr className={`cursor-pointer border-b hover:bg-muted/40 ${selected ? 'bg-muted/60' : ''}`} onClick={onSelect}>
      <td className="py-2 pr-3">
        <span className={`rounded px-2 py-0.5 text-xs ${STATE_STYLE[s.state]}`}>{STATE_LABEL[s.state]}</span>
        {s.lock?.holder === 'human' && <span className="ml-1 text-xs text-amber-700">(@{s.lock.by} at the keyboard)</span>}
        {limit && <div className="mt-1 text-xs text-amber-700">usage limit, resumes {limit.toLocaleTimeString()}</div>}
        {s.handover && <div className="mt-1 text-xs text-blue-700">handing over to {s.handover.toProfile}</div>}
      </td>
      <td className="py-2 pr-3">
        <div className="font-mono text-xs">{r.link ? <Link to={r.link} onClick={(e) => e.stopPropagation()} className="hover:underline">{r.text}</Link> : r.text}</div>
        <div className="max-w-md truncate text-sm" dir="auto">{s.title}</div>
      </td>
      <td className="py-2 pr-3 text-xs">
        {s.role === 'reviewer' ? 'review' : s.role === 'qa' ? 'QA' : 'developer'}
        <div className="font-mono text-muted-foreground">{s.agentProfile}</div>
      </td>
      <td className="py-2 pr-3 text-xs">{s.step || '—'}</td>
      <td className="py-2 pr-3 text-xs">
        {label}
        <div className="mt-1 h-1 w-20 rounded bg-muted">
          <div className={`h-1 rounded ${budgetShare >= 80 ? 'bg-orange-500' : 'bg-primary'}`} style={{ width: `${budgetShare}%` }} />
        </div>
      </td>
      <td className="py-2 text-xs text-muted-foreground">{formatRelativeTime(s.updatedAt)}</td>
    </tr>
  );
}

/** The session's live log, as the runner streams it (polled every few seconds) */
function LiveLog({ id, live }: { id: string; live: boolean }) {
  const [lines, setLines] = useState<LogLine[]>([]);
  const last = useRef(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLines([]);
    last.current = 0;
  }, [id]);

  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const more = await getAgentLog(id, last.current);
        if (stopped || !more.length) return;
        last.current = more[more.length - 1].seq;
        setLines((prev) => [...prev, ...more].slice(-500));
      } catch {
        // the next poll tries again
      }
    };
    load();
    const timer = live ? setInterval(load, 3000) : undefined;
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
  }, [id, live]);

  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [lines]);

  return (
    <div ref={box} className="h-72 overflow-y-auto rounded bg-slate-950 p-2 font-mono text-[11px] leading-relaxed text-slate-100" dir="auto">
      {lines.length === 0 ? (
        <div className="text-slate-400">No log yet.</div>
      ) : (
        lines.map((l) => (
          <div key={l.seq} className="whitespace-pre-wrap">
            <span className="text-slate-500">{new Date(l.at).toLocaleTimeString()} </span>
            {l.text}
          </div>
        ))
      )}
    </div>
  );
}

/** Everyone reads the sessions of their projects; the CEO and the PM message them; the CEO also pauses, resumes and stops */
function SessionDetail({ id, isExecutive, isLead }: { id: string; isExecutive: boolean; isLead: boolean }) {
  const { data: s, isLoading } = useAgent(id);
  const { message, control, dispatch } = useAgentActions();
  const { data: profiles } = useAgentProfiles();
  const [text, setText] = useState('');
  const [note, setNote] = useState<string | null>(null);
  if (isLoading || !s) return <LoadingSpinner />;
  // A failed session can still be resumed; done and stopped ones are over
  const active = !['done', 'cancelled'].includes(s.state);
  const attach = `sudo -u rv-agents rv-attach ${s.sessionId}`;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {ref(s).text} · {s.aiModel}
          {s.effort ? ` (${s.effort})` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
          <div>Branch: <span className="font-mono">{s.branch}</span> → <span className="font-mono">{s.baseBranch}</span></div>
          <div>Turns: {s.turns} · last heartbeat {s.heartbeatAt ? formatRelativeTime(s.heartbeatAt) : '—'}</div>
          {s.worktree && <div className="sm:col-span-2">Worktree: <span className="font-mono">{s.worktree}</span></div>}
          {s.requestedBy?.username && <div>Started by @{s.requestedBy.username}</div>}
          <div>Agent: <span className="font-mono">{s.agentProfile}</span> ({s.billing === 'subscription' ? 'subscription' : 'pay per use'})</div>
        </div>

        <div>
          <div className="mb-1 text-xs font-medium">Live log</div>
          <LiveLog id={s.sessionId} live={s.state === 'running'} />
        </div>
        {s.lastResult && (
          <div className="rounded border bg-muted/30 p-2 text-xs">
            <div className="font-medium">Last turn: {s.lastResult.subtype}</div>
            {s.lastResult.text && <div className="mt-1 whitespace-pre-wrap" dir="auto">{s.lastResult.text}</div>}
          </div>
        )}
        {s.error && <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">{s.error}</div>}

        <div>
          <div className="mb-1 text-xs font-medium">Inbox</div>
          <ul className="max-h-60 space-y-1 overflow-y-auto text-xs">
            {(s.inbox || [])
              .slice()
              .reverse()
              .map((m) => (
                <li key={m.mid} className="rounded border p-2">
                  <span className="font-medium">@{m.from}</span> <span className="text-muted-foreground">({m.source}, {formatRelativeTime(m.at)}{m.deliveredAt ? ', read' : ', pending'})</span>
                  {m.text && <div className="mt-1 line-clamp-4 whitespace-pre-wrap" dir="auto">{m.text}</div>}
                </li>
              ))}
          </ul>
        </div>

        {isLead && active && (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!text.trim()) return;
              message.mutate({ id: s.sessionId, text: text.trim() }, { onSuccess: () => setText('') });
            }}
          >
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message to the session (its next turn)" dir="auto" />
            <Button type="submit" disabled={message.isPending || !text.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </form>
        )}

        {isLead && active && (
          <div className="flex flex-wrap gap-2">
            {isExecutive && s.state !== 'paused' && (
              <Button variant="outline" size="sm" onClick={() => control.mutate({ id: s.sessionId, action: 'pause' })}>
                <Pause className="mr-1 h-3 w-3" /> Pause
              </Button>
            )}
            {['paused', 'failed', 'waiting_human', 'idle'].includes(s.state) && (
              <Button variant="outline" size="sm" onClick={() => control.mutate({ id: s.sessionId, action: 'resume' })}>
                <Play className="mr-1 h-3 w-3" /> Resume
              </Button>
            )}
            {isExecutive && (
              <Button variant="outline" size="sm" onClick={() => control.mutate({ id: s.sessionId, action: 'cancel' })}>
                <Square className="mr-1 h-3 w-3" /> Stop
              </Button>
            )}
            {isExecutive && s.iid && (profiles?.profiles.length || 0) > 1 && !s.handover && (
              <select
                className="h-8 rounded border bg-background px-2 text-xs"
                value=""
                onChange={async (e) => {
                  const profile = e.target.value;
                  if (!profile) return;
                  const outcome = await dispatch.mutateAsync({ project: String(s.gitlabProjectId), iid: s.iid, role: s.role, profile });
                  setNote(outcome.decision === 'executed' ? `It writes a handover note, then ${profile} takes over.` : `Not handed over: ${outcome.denyReason || outcome.decision}`);
                }}
              >
                <option value="">Hand over to…</option>
                {profiles!.profiles
                  .filter((p) => p.name !== s.agentProfile)
                  .map((p) => (
                    <option key={p.name} value={p.name}>
                      {p.name}
                    </option>
                  ))}
              </select>
            )}
          </div>
        )}
        {note && <p className="text-xs text-muted-foreground">{note}</p>}

        <div className="text-xs">
          <div className="mb-1 flex items-center gap-1 font-medium">
            <Terminal className="h-3 w-3" /> Take over on the test server
          </div>
          <code className="block break-all rounded bg-muted p-2">{attach}</code>
          {s.tmux && <code className="mt-1 block rounded bg-muted p-2">sudo -u rv-agents tmux attach -t {s.tmux}</code>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Agents() {
  const { user } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const { data: sessions = [], isLoading } = useAgents(!showAll);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Bot className="h-6 w-6" /> AI sessions
          </h1>
          <p className="text-sm text-muted-foreground">
            Developer and reviewer sessions on the test server. Assign an issue to rv-dev (on a live project) to start one;
            comments mentioning @rv-dev and messages here reach it as its next turn.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show finished
        </label>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">No AI sessions{showAll ? '' : ' running'}.</CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-5">
          <Card className="lg:col-span-3">
            <CardContent className="overflow-x-auto pt-4">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="pb-2 pr-3 font-medium">State</th>
                    <th className="pb-2 pr-3 font-medium">Work</th>
                    <th className="pb-2 pr-3 font-medium">Role</th>
                    <th className="pb-2 pr-3 font-medium">Step</th>
                    <th className="pb-2 pr-3 font-medium">Cost</th>
                    <th className="pb-2 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <SessionRow key={s.sessionId} s={s} selected={selected === s.sessionId} onSelect={() => setSelected(s.sessionId)} />
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <div className="lg:col-span-2">{selected ? <SessionDetail id={selected} isExecutive={user?.role === 'executive'} isLead={user?.role === 'executive' || user?.role === 'pm'} /> : null}</div>
        </div>
      )}
    </div>
  );
}
